import cn from 'classnames';
import { useState } from 'react';

import ICircle from 'components/icons/ICircle';
import Warning from 'components/icons/Warning';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';

export const ERROR_TYPE = 'red';
export const SUCCESS_TYPE = 'green';
export const WARNING_TYPE = 'yellow';
export const INFO_TYPE = 'blue';

const MAX_LENGTH = 65;

const Banner = ({
  title = '',
  type = ERROR_TYPE,
  children,
  className,
  headerClassName = '',
  subtitle,
}) => {
  const isError = type === ERROR_TYPE;
  const isWarning = type === WARNING_TYPE;
  const isInfo = type === INFO_TYPE;
  const { isMobile } = useScreenSizeConstants();

  const [readMore, setReadMore] = useState(false);
  const onClick = () => setReadMore(!readMore);

  const linkName = readMore ? ' ' + 'Read Less' : 'Read More';
  const isCollapsed = isMobile && !readMore && subtitle && subtitle.length > MAX_LENGTH;

  const splitSubtitle = subtitle.substring(0, MAX_LENGTH);
  const collapsedSubtitle = splitSubtitle.substring(0, splitSubtitle.lastIndexOf(' ')) + '... ';
  const shortSubtitle = subtitle.length <= MAX_LENGTH;

  return (
    <aside className={cn(className, 'mt-3 flex w-full')}>
      <div
        className={cn('flex items-center', 'rounded-l', 'p-3', {
          'bg-red-100': isError,
          'bg-yellow-100': isWarning,
          'bg-blue-100': isInfo,
        })}
      >
        {(isError || isWarning) && <Warning className={cn('text-white-100', 'w-6', 'h-6')} />}
        {isInfo && <ICircle className={'mb-0.5 h-6 w-6 align-middle text-white-100'} />}
      </div>
      <div
        className={cn(
          'p-4',
          'bp1:flex',
          'bp1:justify-between',
          'relative',
          'w-full',
          'rounded-r',
          'border-y',
          'border-r',
          'border-solid',
          headerClassName,
          {
            'border-red-100 bg-red-50 text-red-120': isError,
            'border-yellow-100 bg-yellow-50 text-orange-120': isWarning,
            'border-blue-100 bg-blue-50 text-blue-120': isInfo,
          }
        )}
      >
        <hgroup>
          {title && <h1 className="text font-medium">{title}</h1>}
          <h2 className="textSm font-medium">
            {isCollapsed ? collapsedSubtitle : subtitle + ' '}
            {isMobile && !shortSubtitle && (
              <button
                onClick={onClick}
                className={cn('textSm', 'font-medium', 'underline', 'inline-block', {
                  'text-red-120': isError,
                  'text-orange-120': isWarning,
                  'text-blue-120': isInfo,
                })}
              >
                {linkName}
              </button>
            )}
          </h2>
        </hgroup>
        {children}
      </div>
    </aside>
  );
};

Banner.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  subtitle: PropTypes.string.isRequired,
  title: PropTypes.string,
  type: PropTypes.oneOf([ERROR_TYPE, SUCCESS_TYPE, WARNING_TYPE, INFO_TYPE]),
};

export default Banner;
